// Domain labels and small pure helpers shared by the v2 screens.
// Calendar dates from the API ("YYYY-MM-DD") have no time zone: they are
// parsed and shifted as plain dates, never through the browser's zone.
import { todayISO } from "../../helpers/datetime";

export const CHANNELS = [
    { value: "coffee", label: "Coffee", icon: "ri-cup-line" },
    { value: "call", label: "Call", icon: "ri-phone-line" },
    { value: "message", label: "Message", icon: "ri-chat-3-line" },
    { value: "email", label: "Email", icon: "ri-mail-line" },
    { value: "event", label: "Event", icon: "ri-calendar-event-line" },
    { value: "intro_made", label: "Intro made", icon: "ri-share-forward-line" },
    { value: "intro_received", label: "Intro received", icon: "ri-user-received-2-line" },
];

const CHANNEL_MAP = CHANNELS.reduce((m, c) => ({ ...m, [c.value]: c }), {});
export const channelLabel = (v) => (CHANNEL_MAP[v] ? CHANNEL_MAP[v].label : humanize(v));
export const channelIcon = (v) => (CHANNEL_MAP[v] ? CHANNEL_MAP[v].icon : "ri-chat-1-line");

export const TIERS = ["A", "B", "C"];

export const RELATIONSHIP_TYPES = [
    { value: "decision_maker", label: "Decision maker" },
    { value: "connector", label: "Connector" },
    { value: "peer", label: "Peer" },
    { value: "investor", label: "Investor" },
];

export const COMPANY_STATUSES = [
    { value: "prospect", label: "Prospect" },
    { value: "target", label: "Target" },
    { value: "active_client", label: "Active client" },
    { value: "past_client", label: "Past client" },
    { value: "partner", label: "Partner" },
    { value: "inactive", label: "Inactive" },
];

export const SIZE_BANDS = ["1-10", "11-50", "51-200", "201-1000", "1001-5000", "5000+"];

export const SCORE_PARTS = [
    { key: "score_financial", label: "Financial capacity" },
    { key: "score_engagement", label: "Engagement readiness" },
    { key: "score_fit", label: "Strategic fit" },
    { key: "score_access", label: "Accessibility" },
];

export const LINKEDIN_STATUSES = [
    { value: "connected", label: "Connected" },
    { value: "invitation_sent", label: "Invitation sent" },
    { value: "invitation_received", label: "Invitation received" },
];

export const DUE_STATUS = {
    overdue: { label: "Overdue", color: "danger" },
    due_soon: { label: "Due soon", color: "warning" },
    ok: { label: "OK", color: "success" },
};

// "pending_approval" -> "pending approval"
export function humanize(v) {
    if (v === null || v === undefined) return "";
    return String(v).replace(/_/g, " ");
}

export const optionLabel = (options, v) => {
    const o = options.find((x) => x.value === v);
    return o ? o.label : humanize(v);
};

// "Ayşe Çelik" -> "AÇ"; "Acme" -> "AC"
export function initials(name) {
    const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toLocaleUpperCase("tr-TR");
    return (parts[0][0] + parts[parts.length - 1][0]).toLocaleUpperCase("tr-TR");
}

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

const ymdToUTC = (ymd) => {
    const m = YMD.exec(ymd || "");
    if (!m) return null;
    return Date.UTC(+m[1], +m[2] - 1, +m[3]);
};

const utcToYmd = (ms) => new Date(ms).toISOString().slice(0, 10);

// addDays("2026-10-12", 7) -> "2026-10-19"
export function addDays(ymd, n) {
    const t = ymdToUTC(ymd);
    if (t === null) return "";
    return utcToYmd(t + n * 86400000);
}

// Whole days from a to b (b - a); null when either is not a date.
export function daysBetween(a, b) {
    const ta = ymdToUTC(a);
    const tb = ymdToUTC(b);
    if (ta === null || tb === null) return null;
    return Math.round((tb - ta) / 86400000);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// formatDay("2026-10-01") -> "1 Oct 2026"; { short: true } -> "1 Oct" in the
// current year. Accepts only calendar dates; returns fallback otherwise.
export function formatDay(ymd, { short = false, fallback = "", today } = {}) {
    const m = YMD.exec(ymd || "");
    if (!m) return fallback;
    const day = String(+m[3]);
    const mon = MONTHS[+m[2] - 1];
    const thisYear = (today || todayISO()).slice(0, 4);
    if (short && m[1] === thisYear) return `${day} ${mon}`;
    return `${day} ${mon} ${m[1]}`;
}

// relativeDay("2026-10-10", "2026-10-12") -> "2 days ago"
export function relativeDay(ymd, today = todayISO()) {
    const d = daysBetween(ymd, today);
    if (d === null) return "";
    if (d === 0) return "today";
    if (d === 1) return "yesterday";
    if (d === -1) return "tomorrow";
    if (d > 0) return `${d} days ago`;
    return `in ${-d} days`;
}

// Text for a due_in_days value: -4 -> "4 days overdue", 0 -> "due today".
export function dueText(dueInDays) {
    if (dueInDays === null || dueInDays === undefined) return "";
    if (dueInDays < 0) return `${-dueInDays} ${-dueInDays === 1 ? "day" : "days"} overdue`;
    if (dueInDays === 0) return "due today";
    if (dueInDays === 1) return "due tomorrow";
    return `due in ${dueInDays} days`;
}

// Cadence column for a contact or company object.
export function cadenceText(entity) {
    if (!entity || !entity.effective_cadence_days) return "Not kept in touch";
    const days = entity.effective_cadence_days;
    if (entity.cadence_days_override) return `Every ${days}d · custom`;
    if (entity.tier) return `Every ${days}d · tier ${entity.tier}`;
    return `Every ${days}d`;
}

// Snooze presets relative to the Istanbul today.
export const SNOOZE_PRESETS = [
    { label: "1 week", days: 7 },
    { label: "2 weeks", days: 14 },
    { label: "1 month", days: 30 },
    { label: "3 months", days: 90 },
];

export const FOLLOW_UP_PRESETS = [
    { label: "+1 week", days: 7 },
    { label: "+2 weeks", days: 14 },
    { label: "+1 month", days: 30 },
];

// PATCH body with only the keys whose values differ. "" and null are both
// "empty"; an empty edited value is sent as null so the API clears it.
export function diffPatch(original, edited, keys) {
    const out = {};
    (keys || Object.keys(edited)).forEach((k) => {
        const a = original ? original[k] : undefined;
        let b = edited[k];
        const emptyA = a === null || a === undefined || a === "";
        const emptyB = b === null || b === undefined || b === "";
        if (emptyA && emptyB) return;
        if (Array.isArray(a) || Array.isArray(b)) {
            if (JSON.stringify(a || []) === JSON.stringify(b || [])) return;
        } else if (a === b) return;
        out[k] = emptyB ? null : b;
    });
    return out;
}

// Score 0-100 -> design-system color name.
export function scoreColor(score) {
    if (score >= 70) return "success";
    if (score >= 40) return "warning";
    if (score > 0) return "danger";
    return "secondary";
}

export const plural = (n, one, many) => `${n} ${n === 1 ? one : many || one + "s"}`;

// Who hid a record (E12 hidden_source): "Claude", "you", "an import", …
const HIDDEN_BY = { claude: "Claude", ui: "you", import: "an import", manual: "a script", system: "Horus" };
export const hiddenBy = (source) => HIDDEN_BY[source] || source || "";

// "This person is hidden from the People list · hidden on 28 Oct 2026 by Claude".
export const hiddenText = (kind, at, source, fmt) => {
    const list = kind === "company" ? "This company is hidden from the Company list" : "This person is hidden from the People list";
    const when = at ? ` on ${fmt(at)}` : "";
    const by = hiddenBy(source);
    return `${list} · hidden${when}${by ? ` by ${by}` : ""}`;
};
